'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Button, DialogFooter, Step } from '@repo/ui';
import { ActionFlowContext, ActionFlowDialog, ActionFlowDialogWidget, isActionFlowFinalizing, isActionFlowMayStillLand, useActionFlow } from '@repo/web3';
import { useEffect, useRef } from 'react';
import type { ActionFlowTracking } from '@/lib/analytics/client/trackers/action-flow';
import type { CheckoutAttemptTrackingContext, ReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { type ActionFlowSimulationBypass, SimulationGate } from '@/components/ActionFlow/SimulationGate';
import ResultGlow from '@/components/ConfirmDialogV2/ResultGlow';
import ResultHeroIcon from '@/components/ConfirmDialogV2/ResultHeroIcon';
import { useVaultsActionFlowTracking } from '@/components/providers/VaultsActionFlowProvider';
import { useDeferredSubmit } from '@/hooks/useDeferredSubmit';
import { useOnWalletChange } from '@/hooks/useOnWalletChange';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
const REVIEW_STEP_ID = 'review';
export interface ActionFlowExit {
  completed: boolean;
}
export interface ActionFlowReviewDialogProps {
  open: boolean;
  onExit: (exit: ActionFlowExit) => void;
  operationLabel: string;
  isBuilding: boolean;
  isRefreshing?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  reviewTrackingParams: ReviewDialogParams;
  getTracking: (checkout: CheckoutAttemptTrackingContext) => ActionFlowTracking;
  /** Error from the final pre-send transaction simulation. */
  finalSimulationError?: unknown;
  /** Reports final-simulation skipping separately from user-accepted bypass. */
  onSimulationBypassChange?: (bypass: ActionFlowSimulationBypass) => void;
  recap: ReactNode;
}

/**
 * Wrap this dialog in `VaultsActionFlowProvider`, outside the dialog shell per ADR-2026-10-06.
 * Closing the parent by setting `open` to false does not reset the flow, so mount the provider only
 * for the dialog's open lifetime.
 * Build the flow in the same subtree and register it with
 * `useActionFlowRegistration().registerActionFlow(flow)` where it is built.
 */
export function ActionFlowReviewDialog({
  open,
  onExit,
  operationLabel,
  isBuilding,
  isRefreshing,
  isDisabled = false,
  disabledReason,
  reviewTrackingParams,
  getTracking,
  finalSimulationError,
  onSimulationBypassChange,
  recap
}: ActionFlowReviewDialogProps) {
  const actionFlow = useActionFlow();
  const {
    state,
    stepLabels
  } = actionFlow;
  const tracking = useVaultsActionFlowTracking();
  const hasFlow = (actionFlow.callRequests?.length ?? 0) > 0;
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, hasFlow && !isBuilding);
  const hasAttemptedExecution = useRef(false);
  const hasCompletedFlow = useRef(false);
  const isFinalizing = isActionFlowFinalizing(state);
  const isFinalizingLastStep = isFinalizing && state.stepIndex >= stepLabels.length - 1;
  const mayStillLand = isActionFlowMayStillLand(state);
  useEffect(() => {
    if (state.flowState === 'success') hasCompletedFlow.current = true;
  }, [state.flowState]);
  const handleAttempt = (): CheckoutAttemptTrackingContext => {
    hasAttemptedExecution.current = true;
    hasCompletedFlow.current = false;
    return confirmAttempt();
  };
  const handleExit = (completed = hasCompletedFlow.current || isFinalizingLastStep) => {
    if (hasAttemptedExecution.current && !hasCompletedFlow.current && state.flowState === 'idle' && !isFinalizing) {
      tracking.onCancelActiveFlow();
    }
    if (!hasAttemptedExecution.current) trackClose();
    onExit({
      completed
    });
  };
  const exitAndReset = () => {
    const completed_0 = hasCompletedFlow.current || isFinalizingLastStep;
    if (state.flowState === 'execution') actionFlow.abort();
    actionFlow.reset();
    handleExit(completed_0);
  };
  const handleCancelActiveFlow = () => {
    if (isFinalizingLastStep) return;
    tracking.onCancelActiveFlow();
  };
  useOnWalletChange(() => {
    if (mayStillLand) return;
    handleCancelActiveFlow();
    exitAndReset();
  });
  const isBlocked = isBuilding || isDisabled;
  return <ActionFlowDialog open={open} onExit={handleExit} onCancelActiveFlow={handleCancelActiveFlow} fullScreenMobile initialStepId={REVIEW_STEP_ID} className="max-md:inset-x-0 max-md:top-auto max-md:bottom-0 max-md:h-auto max-md:max-h-[90dvh] max-md:w-full max-md:max-w-none max-md:rounded-t-[20px] max-md:rounded-b-none" dataTestId="confirm-dialog">
      {state.flowState === 'success' && <ResultGlow />}
      <Step id={REVIEW_STEP_ID} title={state.flowState === 'success' ? undefined : 'Review'}>
        <div className="gap-md flex flex-col" data-testid="action-flow-recap">
          {state.flowState === 'success' && <div className="pb-md flex justify-center" data-testid="action-flow-success-hero">
              <ResultHeroIcon status="success" />
            </div>}
          {recap}
          {disabledReason && <div className="text-destructive-foreground text-xs" role="alert">
              {disabledReason}
            </div>}
        </div>
        <DialogFooter className="flex-col sm:flex-col">
          <SimulationGate finalSimulationError={finalSimulationError} onSimulationBypassChange={onSimulationBypassChange}>
            <TrackedActionFlowExecution open={open} isBlocked={isBlocked} isRefreshing={Boolean(isRefreshing)} isDisabled={isDisabled} confirmAttempt={handleAttempt} getTracking={getTracking}>
              {hasFlow ? <ActionFlowDialogWidget disabled={isBlocked} /> : <Button size="lg" disabled>
                  {isBuilding ? 'Loading' : operationLabel}
                </Button>}
            </TrackedActionFlowExecution>
          </SimulationGate>
        </DialogFooter>
      </Step>
    </ActionFlowDialog>;
}
function TrackedActionFlowExecution(t0) {
  const $ = _c(22);
  const {
    open,
    isBlocked,
    isRefreshing,
    isDisabled,
    confirmAttempt,
    getTracking,
    children
  } = t0;
  const flow = useActionFlow();
  const {
    setTracking
  } = useVaultsActionFlowTracking();
  const t1 = !isBlocked;
  const t2 = !open || isDisabled;
  let t3;
  if ($[0] !== confirmAttempt || $[1] !== flow || $[2] !== getTracking || $[3] !== setTracking) {
    t3 = () => {
      const checkout = confirmAttempt();
      setTracking(getTracking(checkout));
      return flow.execute();
    };
    $[0] = confirmAttempt;
    $[1] = flow;
    $[2] = getTracking;
    $[3] = setTracking;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isRefreshing || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      isRefreshing,
      canSubmit: t1,
      shouldCancel: t2,
      submit: t3
    };
    $[5] = isRefreshing;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    request: requestExecute,
    isPending
  } = useDeferredSubmit(t4);
  let t5;
  if ($[10] !== requestExecute) {
    t5 = async () => requestExecute();
    $[10] = requestExecute;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = !!flow.isLoading || isPending;
  let t7;
  if ($[12] !== flow || $[13] !== t5 || $[14] !== t6) {
    t7 = {
      ...flow,
      execute: t5,
      isLoading: t6
    };
    $[12] = flow;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== children || $[17] !== isRefreshing) {
    t8 = <div className="gap-2xs flex w-full flex-col" data-testid="action-flow-execution" data-refreshing={isRefreshing}>{children}</div>;
    $[16] = children;
    $[17] = isRefreshing;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <ActionFlowContext.Provider value={t7}>{t8}</ActionFlowContext.Provider>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}